/**
 * Pantallas del arranque del SSO: la espera mientras se resuelve la sesión, el
 * fallo cuando no se pudo resolver, y el acceso denegado por falta de rol.
 *
 * Existen porque el login corre ANTES de montar la app (KEYCLOAK_SSO_SETUP.md
 * §2.5): sin ellas el usuario ve una página en blanco mientras el navegador
 * negocia con Keycloak, y nada en absoluto si esa negociación falla. Se
 * renderizan solas, fuera de `<App />`, así que traen su propia marca.
 */

import React from 'react';
// @ts-ignore
import logoART from '../assets/ART.png';
import type { ErrorSso } from '../config/keycloak.config';
import { getSsoAppsUrl, logout } from '../config/keycloak.config';
import { ROL_INGRESAR } from './usuario';

/** Tarjeta centrada a página completa, con la marca institucional. */
const MarcoSso: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-16">
    <div className="w-full max-w-md">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Franja tricolor institucional */}
        <div className="flex h-1">
          <div className="flex-1 bg-[#FFCD00]" />
          <div className="flex-1 bg-[#0943B5]" />
          <div className="flex-1 bg-[#C8102E]" />
        </div>
        <div className="flex flex-col items-center text-center px-8 py-10">
          <img
            src={logoART}
            alt="Agencia de Renovación del Territorio"
            className="h-10 w-auto object-contain mb-8"
          />
          {children}
        </div>
      </div>
    </div>
  </div>
);

/** Se muestra mientras Keycloak decide si hay sesión o hay que pedir login. */
export const PantallaVerificando: React.FC = () => (
  <MarcoSso>
    <div className="w-9 h-9 border-[3px] border-[#0943B5]/20 border-t-[#0943B5] rounded-full animate-spin mb-5" />
    <h1 className="text-lg font-semibold text-gray-900">Verificando su sesión</h1>
    <p className="text-sm text-gray-500 mt-2">
      Conectando con el sistema de autenticación institucional…
    </p>
  </MarcoSso>
);

/**
 * Fallo del SSO. Sin login no hay aplicación —toda la vista es privada—, así
 * que las únicas acciones ofrecidas son reintentar o volver al portal; no hay
 * modo degradado al que caer.
 */
export const PantallaErrorSso: React.FC<{ error: ErrorSso }> = ({ error }) => (
  <MarcoSso>
    <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mb-5">
      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
      </svg>
    </div>
    <h1 className="text-lg font-semibold text-gray-900">{error.titulo}</h1>
    <p className="text-sm text-gray-500 mt-2">{error.detalle}</p>
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="mt-6 w-full py-2.5 px-4 bg-[#0943B5] hover:bg-[#072d8a] text-white text-sm font-medium rounded-xl transition-colors duration-200"
    >
      Reintentar
    </button>
    <a
      href={getSsoAppsUrl()}
      className="mt-3 text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200"
    >
      Ir al portal de aplicaciones
    </a>
  </MarcoSso>
);

/**
 * Autenticado en el realm, pero sin el rol que habilita esta app.
 *
 * Es un caso esperado, no un error: el realm es compartido por todas las apps
 * del workspace, así que cualquier funcionario con sesión válida puede llegar
 * hasta aquí. Se le nombra el rol que le falta para que sepa qué pedir.
 */
export const PantallaSinAcceso: React.FC = () => (
  <MarcoSso>
    <div className="w-12 h-12 bg-[#FEF6E7] rounded-2xl flex items-center justify-center mb-5">
      <svg className="w-6 h-6 text-[#B26B00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
      </svg>
    </div>
    <h1 className="text-lg font-semibold text-gray-900">Sin acceso a la Biblioteca PQRSD</h1>
    <p className="text-sm text-gray-500 mt-2">
      Su sesión institucional es válida, pero su cuenta no tiene el permiso{' '}
      <code className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{ROL_INGRESAR}</code>{' '}
      para esta aplicación. Solicítelo al administrador del sistema.
    </p>
    <a
      href={getSsoAppsUrl()}
      className="mt-6 block w-full py-2.5 px-4 bg-[#0943B5] hover:bg-[#072d8a] text-white text-sm font-medium rounded-xl transition-colors duration-200"
    >
      Ver todas las aplicaciones
    </a>
    <button
      type="button"
      onClick={logout}
      className="mt-3 text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200"
    >
      Cerrar sesión
    </button>
  </MarcoSso>
);
